import React, { useState, useEffect, useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import moment from 'moment';
import 'react-time-picker/dist/TimePicker.css';

import { useAuth } from '../../../../firebase/AuthProvider';
import { AllEvent, AllUsers } from '../../../../interfaces/identity/calendar/Events';
import { createEvent, getAllEmployees } from '../../../../services/calendarService/EventService';
import { toast } from 'react-toastify';
import Loader from '../../loader/Loader';
import calenderlogo from '../../../../assets/images/calenderlogo.svg';
import SelectDropdown from '../../../ui/Dropdown/SelectDropdown';
import ParticipantSelectDropdown from '../../../ui/Dropdown/ParticipantSelectDropdown';
import Task from '../../../../interfaces/task/Task';
import { GetUserWhoNotHaveEmails } from '../../../../utilities/common/commonFuntion';

interface EventData {
    title: string;
    startTime: string;
    endTime: string;
    description: string;
    location: string;
    meetingDetails: string;
    ownerUserID: string;
    participantUserIds: string[];
    externalUserEmails: string[];
    taskId: number;
    isSprout: boolean;
}

interface AddTaskMeetingModalProps {
    onClose?: () => void;
    onEventCreated?: () => void;
    task: Task;
}

const generateTimeOptions = () => {
    const times = [];
    let time = moment().startOf('day');
    const endOfDay = moment().endOf('day');
    while (time.isSameOrBefore(endOfDay)) {
        times.push(time.format("h:mm A"));
        time.add(15, 'minutes');
    }
    return times;
};

const timeOptions = generateTimeOptions();

const getRoundedCurrentTime = () => {
    const now = moment();
    const roundedMinutes = Math.ceil(now.minute() / 15) * 15;
    return now.startOf('hour').add(roundedMinutes, 'minutes');
};

export default function AddTaskMeetingModal({
    onClose,
    onEventCreated,
    task
}: AddTaskMeetingModalProps) {
    const { currentUser } = useAuth();
    const userId = (currentUser as any)?.uid;
    const token = (currentUser as any)?.accessToken;

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [employees, setEmployees] = useState<AllUsers[]>([]);

    const [selectedDate, setSelectedDate] = useState<string>(moment().format("YYYY-MM-DD"));
    const defaultStartTime = getRoundedCurrentTime().format("h:mm A");
    const defaultEndTime = getRoundedCurrentTime().add(30, 'minutes').format("h:mm A");

    const [formData, setFormData] = useState<EventData>({
        title: '',
        startTime: defaultStartTime,
        endTime: defaultEndTime,
        description: '',
        location: '',
        meetingDetails: '',
        ownerUserID: userId,
        participantUserIds: task.participant ? [String(task.participant.id)] : [],
        externalUserEmails: [],
        taskId: Number(task.id),
        isSprout: true,
    });

    const participantOptions = useMemo(() => {
        if (employees.length > 0 && task.participant) {
            const participantId = String(task.participant.id);
            return employees.filter(
                (e) => (e as any).type !== 'participant' || String((e as any).userId) === participantId
            );
        }
        return employees;
    }, [employees, task]);

    const handleParticipantsChange = (data: { participantUserIds: string[]; externalUserEmails: string[] }) => {
        setFormData(prev => ({
            ...prev,
            participantUserIds: data.participantUserIds,
            externalUserEmails: data.externalUserEmails,
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleTimeChange = (name: string, value: string | number) => {
        if (typeof value !== 'string') return;
        setFormData((prev) => {
            const updatedFormData = { ...prev, [name]: value };
            if (name === "startTime" && value) {
                const startTimeMoment = moment(value, "h:mm A");
                const endTimeMoment = startTimeMoment.add(30, 'minutes');
                updatedFormData.endTime = endTimeMoment.format("h:mm A");
            }
            return updatedFormData;
        });
    };

    async function fetchAllEmployee() {
        if (!currentUser) return;
        setLoading(true);
        try {
            const response = await getAllEmployees(token);
            if (response) {
                setEmployees(response);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    }

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.startTime) newErrors.startTime = 'Start time is required';
        if (!formData.endTime) newErrors.endTime = 'End time is required';
        // if (formData.participantUserIds.length === 0 && formData.externalUserEmails.length === 0)
        //     newErrors.participantUserIds = "At least one participant is required";
        if (!selectedDate) newErrors.meetingDate = 'Meeting Date is required';

        // Validate that meeting is not in the past
        if (selectedDate && formData.startTime) {
            const meetingDateTime = moment(`${selectedDate} ${formData.startTime}`, "YYYY-MM-DD h:mm A");
            const now = moment();
            
            if (meetingDateTime.isBefore(now)) {
                newErrors.startTime = 'Cannot create meetings in the past';
            }
        }

        // validate no email 
        if (GetUserWhoNotHaveEmails(employees, formData.participantUserIds).length > 0) {
            return newErrors.participantUserIds = "SomeParticipantNoEmail";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (!token) {
            console.error("Missing auth token.");
            return;
        }

        const startTime24hr = moment(formData.startTime, "h:mm A").format("HH:mm");
        const endTime24hr = moment(formData.endTime, "h:mm A").format("HH:mm");

        // Create moments in local timezone
        const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const startMoment = moment.tz(`${selectedDate} ${startTime24hr}`, currentTimeZone);
        let endMoment = moment.tz(`${selectedDate} ${endTime24hr}`, currentTimeZone);
        
        // If end time is less than or equal to start time, add 1 day to endMoment
        if (endMoment.isSameOrBefore(startMoment)) {
            endMoment.add(1, "day");
        }

        // Send the exact date/time as picked by frontend in ISO format (YYYY-MM-DDTHH:mm:ss.sssZ)
        // Format as ISO string with milliseconds and 'Z' to indicate UTC (but actual timezone is in time_zone field)
        // Backend expects: 2026-01-16T20:45:00.000Z format
        // Backend will return the same date/time, and frontend will convert to local for display
        const eventDataToSend = {
            ...formData,
            startTime: startMoment.format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
            endTime: endMoment.format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
            time_zone: currentTimeZone,
        };
        setLoading(true);
        try {
            const result = await createEvent(eventDataToSend, token);
            if (result.success) {
                toast.success(result.message);
                if (onEventCreated) onEventCreated();
                if (onClose) onClose();
            } else {
                toast.error(result.message || "Failed to create event.");
            }
        } catch (error) {
            console.error("Error submitting event:", error);
            toast.error("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllEmployee();
    }, []);

    const timeObjectOptions = timeOptions.map(time => ({ label: time, value: time }));
    const formattedDisplayDate = moment(selectedDate).format("dddd, MMMM DD, YYYY");

    return (
        <>
            {loading && <Loader />}
            <div className=''>
                <div className="top_bar_modal top_bar_modal_flx">
                    <div className="top_bar_modal_head">
                        <h2>Create New Meeting</h2>
                        <p>{formattedDisplayDate}</p>
                    </div>
                    <div className="sprout-toggle-wrapper">
                        <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, isSprout: !prev.isSprout }))}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: formData.isSprout ? "#776ce6" : "#fff",
                                color: formData.isSprout ? "#fff" : "#000",
                                border: `1px solid ${formData.isSprout ? "#776ce6" : "#ccc"}`,
                                borderRadius: "4px",
                                padding: "0.5rem 1rem",
                                cursor: "pointer",
                            }}
                        >
                            <img src={calenderlogo} alt="Sprout Logo" style={{ height: 20, marginRight: 4 }} />
                            Meeting
                        </button>
                    </div>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-12">
                            <div className='custom_input_flled'>
                                <label>Title</label>
                                <input
                                    type="text"
                                    name="title"
                                    placeholder="Title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    style={{ width: '100%' }}
                                />
                                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                            </div>
                        </div>
                        <div className="col-md-12">
                            <div className="custom_input_flled">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Date</label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    min={moment().format("YYYY-MM-DD")}
                                    className="mt-1 block w-full px-3 py-2 border-gray-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors duration-200"
                                />
                                {errors.meetingDate && <p className="text-red-500 text-sm mt-1">{errors.meetingDate}</p>}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <SelectDropdown
                                label="Start Time"
                                name="startTime"
                                value={formData.startTime}
                                onChange={(value) => handleTimeChange("startTime", value)}
                                options={timeObjectOptions}
                                error={errors.startTime}
                            />
                        </div>
                        <div className="col-md-6">
                            <SelectDropdown
                                label="End Time"
                                name="endTime"
                                value={formData.endTime}
                                onChange={(value) => handleTimeChange("endTime", value)}
                                options={timeObjectOptions}
                                error={errors.endTime}
                            />
                        </div>
                        {!formData.isSprout ? (
                            <div className="col-md-12">
                                <div className='custom_input_flled'>
                                    <label>Location</label>
                                    <input
                                        type="text"
                                        name="location"
                                        placeholder="Location"
                                        value={formData.location}
                                        onChange={handleChange}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="col-md-12 mt-3">
                                <div className="custom_input_flled mb-2">
                                    <label>Meeting</label>
                                    <p className="text-muted italic text-xs">A meeting link will be auto-generated upon creation.</p>
                                </div>
                            </div>
                        )}
                        <div className="col-md-12">
                            <ParticipantSelectDropdown
                                label="Participants"
                                name="participants"
                                participantUserIds={formData.participantUserIds}
                                externalUserEmails={formData.externalUserEmails}
                                onChange={handleParticipantsChange}
                                options={participantOptions}
                                error={errors.participantUserIds}
                            />
                        </div>
                    </div>
                    <div className='custom_input_flled mb-0'>
                        <label>Description</label>
                        <textarea
                            name="description"
                            placeholder="Description"
                            value={formData.description}
                            onChange={handleChange}
                        />
                    </div>
                    <div className='modal_footer'>
                        <Button variant='secondary' onClick={onClose}>Cancel</Button>
                        <Button variant="primary" type="submit">Save</Button>
                    </div>
                </form>
            </div>
        </>
    );
}